import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
    Search,
    RefreshCw,
    Filter,
    MoreVertical,
    Table,
    LayoutGrid,
    Phone,
    Mail,
    FileText,
    Upload,
    Download,
    ChevronLeft,
    ChevronRight,
    ChevronDown,
    ArrowRight,
    UserPlus
} from 'lucide-react';
import { TfiLayoutColumn3 } from "react-icons/tfi";
import { User, Calendar as CalendarIcon, Clock } from 'lucide-react';
import { Icon } from '@iconify/react';
import { Checkbox, Box, Typography, Tooltip } from '@mui/material';
import Enquiryoperations from './Enquiryoperations';
import '../EnquiriesModule.css';

const API_SORT_KEYS = {
    enquiryDetails: 'PersonName',
    mobileNumber: 'MobileNo',
    email: 'EmailID',
    creationDetails: 'CreatedDate',
    status: 'Isopen',
    owner: 'User_login',
    remarks: 'Remarks',
    source: 'SourceName',
    country: 'Country',
    state: 'State',
    city: 'City',
    campaign: 'CampaignName',
    medium: 'MediumName',
    type: 'Isopen',
    stages: 'Stages',
    createdBy: 'CreatedBy',
    enquiryId: 'EnquiryId',
    leadNo: 'LeadNo'
};


const EnquiryTable = ({
    enquiries,
    selectedLead,
    visibleColumns: propVisibleColumns,
    setVisibleColumns,
    selectedEnquiries,
    setSelectedEnquiries,
    isAllSelectedAcrossPages,
    setIsAllSelectedAcrossPages,
    excludedEnquiries,
    setExcludedEnquiries,
    onSelectEnquiry,
    searchText,
    onSearchChange,
    onSearch,
    viewMode,
    setViewMode,
    customFieldsList = [],
    totalRecord,
    sortConfig,
    setSortConfig,
    itemsPerPage,
    setItemsPerPage,
    setEnquiries,
    onPageChange,
    onShowDetails,
    currentPage: externalCurrentPage,
    onPdfClick,
    onInitiateCall,
    isHiddenField
}) => {
    const [currentPage, setCurrentPage] = useState(externalCurrentPage || 1);
    const [isScheduleCallOpen, setIsScheduleCallOpen] = useState(false);
    const [selectedIdForCall, setSelectedIdForCall] = useState(null);
    const toolbarMenuRef = useRef(null);
    const massOperationRef = useRef(null);
    const columnSearchRef = useRef(null);

    const visibleColumns = useMemo(() => {
        const result = {};
        for (const [key, isVisible] of Object.entries(propVisibleColumns || {})) {
            // Also check 'address' mapping for custom keys if needed, else fallback
            result[key] = isVisible && (!isHiddenField || !isHiddenField(API_SORT_KEYS[key] || key));
        }
        return result;
    }, [propVisibleColumns, isHiddenField]);

    // Calculate total pages
    const totalPages = Math.ceil(totalRecord / itemsPerPage);

    // Sync external currentPage with internal state
    useEffect(() => {
        if (externalCurrentPage && externalCurrentPage !== currentPage) {
            setCurrentPage(externalCurrentPage);
        }
    }, [externalCurrentPage]);

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedEnquiries(enquiries.map(enq => enq.EnquiryId));
            setExcludedEnquiries([]);
        } else {
            setSelectedEnquiries([]);
            setIsAllSelectedAcrossPages(false);
            setExcludedEnquiries([]);
        }
    };

    const handleSelectEnquiry = (enquiryId) => {
        if (isAllSelectedAcrossPages) {
            const newExcluded = excludedEnquiries.includes(enquiryId)
                ? excludedEnquiries.filter(id => id !== enquiryId)
                : [...excludedEnquiries, enquiryId];
            setExcludedEnquiries(newExcluded);
        } else {
            const newSelected = selectedEnquiries.includes(enquiryId)
                ? selectedEnquiries.filter(id => id !== enquiryId)
                : [...selectedEnquiries, enquiryId];


            setSelectedEnquiries(newSelected);
            if (newSelected.length < enquiries.length) {
                setIsAllSelectedAcrossPages(false);
            }
        }
    };

    const getAvatarStyle = (name) => {
        const palettes = [
            { bg: '#eff6ff', text: '#3b82f6', border: '#dbeafe' }, // Blue
            { bg: '#ecfdf5', text: '#10b981', border: '#d1fae5' }, // Green
            { bg: '#fff1f2', text: '#f43f5e', border: '#ffe4e6' }, // Rose
            { bg: '#fffbeb', text: '#f59e0b', border: '#fef3c7' }, // Amber
            { bg: '#f5f3ff', text: '#8b5cf6', border: '#ede9fe' }, // Violet
            { bg: '#ecfeff', text: '#06b6d4', border: '#cffafe' }, // Cyan
            { bg: '#f0fdf4', text: '#16a34a', border: '#dcfce7' }, // Green 2
            { bg: '#faf5ff', text: '#9333ea', border: '#f3e8ff' }, // Purple
            { bg: '#fff7ed', text: '#ea580c', border: '#ffedd5' }, // Orange
            { bg: '#fdf2f8', text: '#db2777', border: '#fce7f3' }  // Pink
        ];
        if (!name || name === '-') return palettes[0];

        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        const index = Math.abs(hash) % palettes.length;
        return palettes[index];
    };

    const getInitials = (name) => {
        if (!name || name === '-') return 'U';
        let trimmed = String(name).trim();

        // Remove leading dots or special characters (except + for phone numbers)
        if (!trimmed.startsWith('+')) {
            trimmed = trimmed.replace(/^[^a-zA-Z0-9]+/, '');
        }

        if (!trimmed) return 'U';

        if (trimmed.startsWith('+')) {
            // Show first two digits after the +
            return trimmed.replace('+', '').substring(0, 2);
        }

        // If it starts with a number, just return the first two digits
        if (/^\d/.test(trimmed)) {
            return trimmed.substring(0, 2);
        }

        const parts = trimmed.split(/\s+/);
        if (parts.length === 1) return parts[0][0].toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    const handleColumnToggle = (columnKey) => {
        // Prevent unchecking required columns
        if (columnKey === 'enquiryDetails' && visibleColumns[columnKey]) {
            return;
        }

        setVisibleColumns(prev => ({
            ...prev,
            [columnKey]: !prev[columnKey]
        }));
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            if (onPageChange) {
                onPageChange(page);
            }
        }
    };

    const handleSort = (key) => {
        const sortKey = API_SORT_KEYS[key] || key;
        let direction = 'desc';
        if (sortConfig.key === sortKey && sortConfig.direction === 'desc') {
            direction = 'asc';
        }
        setSortConfig({ key: sortKey, direction });
    };


    const getColumnLabel = (key) => {
        if (key.startsWith('custom_')) {
            const field = customFieldsList.find(f => f.key === key);
            return field ? field.label.toUpperCase() : key.replace('custom_', '').toUpperCase();
        }
        switch (key) {
            case 'enquiryDetails': return 'ENQUIRY DETAILS';
            case 'mobileNumber': return 'MOBILE NUMBER';
            case 'email': return 'EMAIL';
            case 'remarks': return 'REMARKS';
            case 'enquiryId': return 'ENQUIRY ID';
            case 'leadNo': return 'LEAD NO';
            case 'status': return 'STATUS';
            case 'score': return 'SCORE';
            case 'creationDetails': return 'CREATION DETAILS';
            case 'source': return 'SOURCE';
            case 'country': return 'COUNTRY';
            case 'state': return 'STATE';
            case 'city': return 'CITY';
            case 'address': return 'ADDRESS';
            case 'medium': return 'MEDIUM';
            case 'type': return 'TYPE';
            case 'campaign': return 'CAMPAIGN';
            case 'isduplicateEmail': return 'ISDUPLICATE EMAIL';
            case 'isduplicateMobile': return 'ISDUPLICATE MOBILE';
            case 'stages': return 'STAGES';
            case 'stagesId': return 'STAGES ID';
            case 'owner': return 'OWNER';
            case 'createdBy': return 'CREATED BY';
            default: return key.replace(/([A-Z])/g, ' $1').trim().toUpperCase();
        }
    };

    const getSortIcon = (key) => {
        const sortKey = API_SORT_KEYS[key] || key;

        if (sortConfig.key !== sortKey) {
            return <Icon icon="ri:arrow-up-down-fill" className="text-gray-300 group-hover:text-blue-400 transition" width={22} />;
        }
        return sortConfig.direction === 'asc'
            ? <Icon icon="ri:arrow-up-s-fill" className="text-blue-600" width={22} />
            : <Icon icon="ri:arrow-down-s-fill" className="text-blue-600" width={22} />;
    };


    return (
        <div className="flex flex-col bg-white h-full w-full">
            {/* Table Area */}
            <div className="flex-1 overflow-auto bg-gray-50/30">
                <table className="w-full border-separate border-spacing-0">
                    <thead className="sticky top-0 z-20 bg-white">
                        <tr>
                            <th className="px-4 py-3 border-b border-gray-200 text-left w-12 bg-white">
                                <Checkbox
                                    size="small"
                                    indeterminate={
                                        (selectedEnquiries.length > 0 && selectedEnquiries.length < enquiries.length && !isAllSelectedAcrossPages) ||
                                        (isAllSelectedAcrossPages && excludedEnquiries.length > 0 && excludedEnquiries.length < totalRecord)
                                    }
                                    checked={
                                        (enquiries.length > 0 && selectedEnquiries.length === enquiries.length && !isAllSelectedAcrossPages) ||
                                        (isAllSelectedAcrossPages && enquiries.every(enq => !excludedEnquiries.includes(enq.EnquiryId)))
                                    }
                                    onChange={handleSelectAll}
                                    sx={{ p: 0, color: '#cbd5e1', '&.Mui-checked': { color: '#00897b' } }}
                                />
                            </th>
                            {Object.entries(visibleColumns).map(([key, isVisible]) => (
                                isVisible && (
                                    <th
                                        key={key}
                                        className="premium-table-header px-4 py-4 text-left group cursor-pointer hover:bg-blue-50/50 transition-all border-b border-gray-200"
                                        onClick={() => handleSort(key)}
                                    >
                                        <Tooltip title="Sort by ascending/descending" arrow placement="top">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-[13px] font-bold text-[#666666] uppercase tracking-wider">
                                                    {getColumnLabel(key)}
                                                </span>
                                                {getSortIcon(key)}
                                            </div>
                                        </Tooltip>
                                    </th>
                                )
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {enquiries.map((enquiry) => {
                            const avatarStyle = getAvatarStyle(enquiry.PersonName);
                            return (
                                <tr
                                    key={enquiry.EnquiryId}
                                    onClick={() => onSelectEnquiry(enquiry)}
                                    className={`group hover:bg-blue-50/30 transition cursor-pointer ${selectedLead === enquiry.EnquiryId ? 'bg-blue-50/50' : ''}`}
                                >
                                    <td className="px-4 py-3 border-b border-gray-100" onClick={e => e.stopPropagation()}>
                                        <Checkbox
                                            size="small"
                                            checked={isAllSelectedAcrossPages ? !excludedEnquiries.includes(enquiry.EnquiryId) : selectedEnquiries.includes(enquiry.EnquiryId)}
                                            onChange={() => handleSelectEnquiry(enquiry.EnquiryId)}
                                            sx={{ p: 0, color: '#cbd5e1', '&.Mui-checked': { color: '#00897b' } }}
                                        />
                                    </td>
                                    {visibleColumns.enquiryDetails && (
                                        <td className="px-4 py-4 border-b border-gray-100 min-w-[200px]">
                                            <div className="flex items-center gap-3">
                                                <div
                                                    className="relative group/avatar cursor-pointer"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onShowDetails(enquiry);
                                                    }}
                                                >
                                                    {(() => {
                                                        const isNoName = !enquiry.PersonName || enquiry.PersonName === '-' || enquiry.PersonName.trim() === '';
                                                        return (
                                                            <div
                                                                className="w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-bold border-2 border-white shadow-sm transition-all group-hover/avatar:scale-105"
                                                                style={{
                                                                    backgroundColor: isNoName ? '#f8fafc' : avatarStyle.bg,
                                                                    color: isNoName ? '#94a3b8' : avatarStyle.text,
                                                                    border: `1px solid ${isNoName ? '#e2e8f0' : avatarStyle.border}`
                                                                }}
                                                            >
                                                                {isNoName ? (
                                                                    <User size={18} strokeWidth={2.5} />
                                                                ) : (
                                                                    getInitials(enquiry.PersonName)
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                                <div className="flex flex-col min-w-0 flex-1">
                                                    <span
                                                        className="ph-name-link text-[14px] font-semibold truncate"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onShowDetails(enquiry);
                                                        }}
                                                    >
                                                        {enquiry.PersonName}
                                                    </span>
                                                </div>
                                                <FileText
                                                    size={16}
                                                    className="text-red-400 cursor-pointer hover:scale-125 transition-transform flex-shrink-0"
                                                    title="Download PDF"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onPdfClick && onPdfClick(enquiry);
                                                    }}
                                                />
                                            </div>
                                        </td>
                                    )}
                                    {visibleColumns.mobileNumber && (
                                        <td className="px-4 py-4 border-b border-gray-100">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    className="hover:scale-110 transition-transform p-1 rounded-full hover:bg-gray-100"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedIdForCall(enquiry.EnquiryId);
                                                        setIsScheduleCallOpen(true);
                                                    }}
                                                >
                                                    <Icon icon="material-symbols:add-call-outline" className="text-slate-400 hover:text-blue-600" width={18} />
                                                </button>
                                                <span
                                                    className="ph-name-link truncate font-semibold"
                                                    onClick={(e) => onInitiateCall(enquiry, e)}
                                                >
                                                    {enquiry.CsvMobileNo}
                                                </span>
                                            </div>
                                        </td>
                                    )}
                                    {visibleColumns.email && (
                                        <td className="px-4 py-4 border-b border-gray-100">
                                            <span className="text-[13px] text-black font-semibold truncate block max-w-[200px]" title={enquiry.CsvEmailId}>
                                                {enquiry.CsvEmailId || '-'}
                                            </span>
                                        </td>
                                    )}
                                    {visibleColumns.enquiryId && <td className="p-4 border-b border-gray-100 text-[11px] font-mono text-gray-400">ID: {enquiry.EnquiryId || '-'}</td>}
                                    {visibleColumns.leadNo && <td className="p-4 border-b border-gray-100 text-[11px] font-semibold text-gray-700">{enquiry.LeadNo || '-'}</td>}
                                    {visibleColumns.status && (
                                        <td className="p-4 border-b border-gray-100">
                                            <span className={`premium-badge-dark ${String(enquiry.IsOpen).toLowerCase() === 'true' || enquiry.IsOpen === true ? 'badge-open' : 'badge-lead'}`}>
                                                {String(enquiry.IsOpen).toLowerCase() === 'true' || enquiry.IsOpen === true ? 'OPEN' : 'LEAD'}
                                            </span>
                                        </td>
                                    )}
                                    {visibleColumns.score && (
                                        <td className="p-4 border-b border-gray-100">
                                            <div className="flex items-center gap-1.5">
                                                <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center text-[10px] font-bold text-blue-600 border border-blue-100">
                                                    {enquiry.CurrentScore || '0'}
                                                </div>
                                                <span className="text-[10px] text-gray-400 font-medium">Score</span>
                                            </div>
                                        </td>
                                    )}
                                    {visibleColumns.creationDetails && (
                                        <td className="px-4 py-4 border-b border-gray-100 min-w-[150px]">
                                            <div className="flex flex-col gap-1 py-1">
                                                <div className="flex items-center gap-2 whitespace-nowrap">
                                                    <span className="text-[11px] font-bold text-slate-700">Date</span>
                                                    <span className="ph-created-date !text-[14px] text-[#334155] font-semibold">
                                                        {enquiry.CreatedDate ? (
                                                            `${new Date(enquiry.CreatedDate).toLocaleDateString('en-GB', {
                                                                day: '2-digit',
                                                                month: 'short',
                                                                year: 'numeric'
                                                            }).replace(/ /g, '-')} ${new Date(enquiry.CreatedDate).toLocaleTimeString('en-GB', {
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                                second: '2-digit',
                                                                hour12: false
                                                            })}`
                                                        ) : '-'}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                    )}
                                    {visibleColumns.remarks && (
                                        <td className="px-4 py-4 border-b border-gray-100 min-w-[150px]">
                                            <div className="flex flex-col">
                                                <span className="text-[11px] font-bold text-slate-700">Remarks</span>
                                                <span className="text-[12px] text-black font-semibold truncate max-w-[150px]" title={enquiry.Remarks}>
                                                    {enquiry.Remarks || 'No remarks'}
                                                </span>
                                            </div>
                                        </td>
                                    )}
                                    {visibleColumns.source && (
                                        <td className="p-4 border-b border-gray-100 text-[12px] text-black font-semibold">
                                            <span className="px-2 py-0.5 bg-gray-100 text-black border border-gray-300 text-[11px] font-bold tracking-tight" style={{ borderRadius: '8px' }}>{enquiry.Source || '-'}</span>
                                        </td>
                                    )}
                                    {visibleColumns.country && <td className="p-4 border-b border-gray-100 text-[12px] text-black font-semibold">{enquiry.Country || '-'}</td>}
                                    {visibleColumns.state && <td className="p-4 border-b border-gray-100 text-[12px] text-black font-semibold">{enquiry.State || '-'}</td>}
                                    {visibleColumns.city && <td className="p-4 border-b border-gray-100 text-[12px] text-black font-semibold">{enquiry.City || '-'}</td>}
                                    {visibleColumns.address && (
                                        <td className="p-4 border-b border-gray-100 text-[12px] text-black font-semibold">
                                            <p className="truncate max-w-[150px]" title={enquiry.Address}>{enquiry.Address || '-'}</p>
                                        </td>
                                    )}
                                    {visibleColumns.medium && <td className="p-4 border-b border-gray-100 text-[12px] text-black font-semibold">{enquiry.Medium || '-'}</td>}
                                    {visibleColumns.type && (
                                        <td className="p-4 border-b border-gray-100">
                                            <span className={`premium-badge-dark ${String(enquiry.IsOpen).toLowerCase() === 'true' || enquiry.IsOpen === true ? 'badge-type-open' : 'badge-type-lead'}`}>
                                                {String(enquiry.IsOpen).toLowerCase() === 'true' || enquiry.IsOpen === true ? 'OPEN' : 'LEAD'}
                                            </span>
                                        </td>
                                    )}
                                    {visibleColumns.campaign && <td className="p-4 border-b border-gray-100 text-[12px] text-black font-semibold">{enquiry.CampaignName || '-'}</td>}
                                    {visibleColumns.isduplicateEmail && (
                                        <td className="p-4 border-b border-gray-100 text-[12px] font-bold">
                                            {enquiry.IsduplicateEmail ? <span className="text-red-500">Duplicate</span> : <span className="text-black">Unique</span>}
                                        </td>
                                    )}
                                    {visibleColumns.isduplicateMobile && (
                                        <td className="p-4 border-b border-gray-100 text-[12px] font-bold">
                                            {enquiry.IsduplicateMobile ? <span className="text-red-500">Duplicate</span> : <span className="text-black">Unique</span>}
                                        </td>
                                    )}
                                    {visibleColumns.stages && (
                                        <td className="p-4 border-b border-gray-100">
                                            <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-bold border border-blue-100 tracking-wider" style={{ borderRadius: '8px' }}>{enquiry.Stages ? enquiry.Stages.toUpperCase() : (enquiry.Stage ? enquiry.Stage.toUpperCase() : 'RAW')}</span>
                                        </td>
                                    )}
                                    {visibleColumns.stagesId && <td className="p-4 border-b border-gray-100 text-[12px] text-black font-semibold font-mono">{enquiry.StagesId || '0'}</td>}
                                    {visibleColumns.owner && (
                                        <td className="p-4 border-b border-gray-100 text-[13.5px] font-semibold text-slate-600">
                                            {enquiry.OwnerId || '-'}
                                        </td>
                                    )}
                                    {visibleColumns.createdBy && (
                                        <td className="p-4 border-b border-gray-100">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-400 flex-shrink-0">
                                                    <User size={16} />
                                                </div>
                                                <span className="text-[14.5px] font-semibold text-[#333155]">{enquiry.OwnerName || '-'}</span>
                                            </div>
                                        </td>
                                    )}
                                    {/* Dynamic custom columns */}
                                    {Object.entries(visibleColumns).map(([key, isVisible]) => {
                                        if (isVisible && key.startsWith('custom_')) {
                                            const field = customFieldsList.find(f => f.key === key);
                                            const fieldLabel = field ? field.label : '';
                                            return (
                                                <td key={key} className="p-4 border-b border-gray-100 text-[12px] text-black font-semibold">
                                                    {enquiry[fieldLabel] || '-'}
                                                </td>
                                            );
                                        }
                                        return null;
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            <div className="px-6 py-4 border-t border-gray-100 bg-white flex items-center justify-end gap-10">
                <div className="flex items-center gap-3">
                    <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Go to page</span>
                    <input
                        type="number"
                        value={currentPage}
                        min={1}
                        max={totalPages}
                        onChange={(e) => handlePageChange(Number(e.target.value))}
                        className="w-14 h-8 text-center text-xs font-bold border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all"
                    />
                </div>

                <span className="text-[11px] font-bold text-gray-500 tracking-tighter">
                    {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, totalRecord)} of {totalRecord}
                </span>

                <div className="flex items-center gap-1">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`p-2 rounded-lg transition-all ${currentPage === 1 ? 'text-gray-200' : 'text-gray-500 hover:bg-gray-50 hover:text-blue-600'}`}
                    >
                        <ChevronLeft size={20} />
                    </button>
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`p-2 rounded-lg transition-all ${currentPage === totalPages ? 'text-gray-200' : 'text-gray-500 hover:bg-gray-50 hover:text-blue-600'}`}
                    >
                        <ChevronRight size={20} />
                    </button>
                </div>
            </div>

            {isScheduleCallOpen && (
                <Enquiryoperations
                    isOpen={isScheduleCallOpen}
                    onClose={() => {
                        setIsScheduleCallOpen(false);
                        setSelectedIdForCall(null);
                    }}
                    leadId={selectedIdForCall}
                />
            )}
        </div>
    );
};

export default EnquiryTable;
